import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Users, Calendar, Globe, Briefcase, CheckCircle } from 'lucide-react';

const GroupCharterPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const useCases = [
    { icon: <Briefcase className="w-6 h-6" />, title: 'Corporate Travel', desc: 'Move entire teams efficiently for meetings, conferences and retreats.' },
    { icon: <Users className="w-6 h-6" />, title: 'Sports Teams', desc: 'Coordinated travel for athletes with equipment handling.' },
    { icon: <Calendar className="w-6 h-6" />, title: 'Special Events', desc: 'Concert tours, film productions, and milestone celebrations.' },
    { icon: <Globe className="w-6 h-6" />, title: 'Incentive Travel', desc: 'Reward top performers with exclusive group travel experiences.' },
  ];

  return (
    <div data-testid="group-charter-page" className="bg-white">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/gc01.jpg"
            alt="Singapore Changi Airport"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 container-padding w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-blue-700 text-sm font-medium tracking-widest uppercase mb-6">
              Our Services
            </span>
            <h1 
              data-testid="service-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Group Charter
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl mb-8">
              High capacity aircraft solutions for corporate groups, sports teams and special events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-enquiry-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
              >
                Request Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+6581888023"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-white/20 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                +65 8188 8023
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-gap">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 
                className="heading-lg text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Move Your Team Together
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="body-large mb-6">
                Moving 20 or 200+ people? Singapore Air Charter can deliver large capacity aircraft solutions that keep everyone together.
              </p>
              <p className="body-default mb-6">
                VIP configured airliners, first, business, premium and standard economy layouts to all-white no-brand aircraft for total discretion. Singapore Air Charter has had the privilege of providing complete aviation solutions for presidents on multi-nation state visits, rock bands we all know and love on tour and business conglomerates who keep coming back, year after year for top-level management and incentive trips.
              </p>
              <p className="body-default mb-8">
                And for on-the-spot service, one of our team will travel with your party if required. Whatever the mission, let Singapore Air Charter handle the logistics so you can focus on the event.
              </p>
              <Link
                to="/contact"
                data-testid="content-cta-btn"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-slate-800 transition-all duration-300"
              >
                Plan Your Group Travel
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="/gc2.jpg"
                alt="Changi Airport"
                className="w-full h-auto object-contain"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">Applications</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Group Travel Solutions
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {useCases.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 text-center border border-slate-100"
              >
                <div className="text-blue-700 mb-4 flex justify-center">{item.icon}</div>
                <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-gap">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 
              className="heading-lg text-slate-900 mb-6 text-center"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              What We Provide
            </h2>
            <div className="gold-line mx-auto mb-8"></div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">Aircraft from 20 to 200+ passengers</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">Customized catering and beverage service</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">Equipment and luggage handling</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">Coordinated ground transportation</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">On-board team member if required</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 
              className="heading-lg text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Planning a Group Trip?
            </h2>
            <p className="body-large mb-10 max-w-2xl mx-auto">
              Let us handle the logistics while you focus on your team. Get a quote today.
            </p>
            <Link
              to="/contact"
              data-testid="final-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
            >
              Get Group Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GroupCharterPage;
